import { useCallback, useEffect, useState } from 'react'
import type { WebAutomation } from '@shared/web'
import { templateInfo } from '@shared/templates'
import { listAutomations } from '../lib/api'

interface Props {
  onOpen: (automation: WebAutomation) => void
}

const CATEGORY_ICONS: Record<string, string> = {
  Spreadsheets: '📊',
  Monitoring: '📡',
  Backup: '🗄️',
  Maintenance: '🧹',
  Integration: '🔗'
}

export default function HomePage({ onOpen }: Props): JSX.Element {
  const [automations, setAutomations] = useState<WebAutomation[] | null>(null)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async (refresh = false) => {
    setRefreshing(true)
    setError('')
    try {
      setAutomations(await listAutomations(refresh))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your automations.')
    } finally {
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Your automations</h1>
          <p className="subtitle">
            These run automatically on their own schedule. Open one to send it files or check its
            latest results.
          </p>
        </div>
        <button className="btn subtle" onClick={() => void load(true)} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}

      {automations === null && !error && (
        <div className="center-note">Loading your automations…</div>
      )}

      {automations !== null && automations.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            🤖
          </div>
          <h2>No automations here yet</h2>
          <p>
            Your administrator sets automations up in the Autom8 admin app. Once one is deployed
            and you have been given access to it, it will show up here.
          </p>
        </div>
      )}

      <div className="card-grid">
        {(automations ?? []).map((a) => {
          const info = templateInfo(a.templateId)
          return (
            <button key={a.jobId} className="automation-card" onClick={() => onOpen(a)}>
              <div className="card-top">
                <span className="card-icon" aria-hidden="true">
                  {CATEGORY_ICONS[info?.category ?? ''] ?? '⚙️'}
                </span>
                {a.paused === true && <span className="chip paused">Paused</span>}
                {a.acceptsFiles && <span className="chip files">Takes files</span>}
              </div>
              <div className="card-name">{a.name}</div>
              <div className="card-blurb">{info?.blurb ?? ''}</div>
            </button>
          )
        })}
      </div>
    </>
  )
}
